import releaseNotesData from './releaseNotesData'

export type ChangeGroups = Record<string, string[]>
export type LocalizedChanges = Record<string, ChangeGroups>

export interface FileReleaseNote {
  source?: string
  version: string
  date: string | null
  changes: LocalizedChanges
}

export interface ReleaseNote {
  source?: string
  version: string
  date: string | null
  changes: LocalizedChanges
}

export const CHANGE_TYPE_LABELS: Record<string, Record<string, string>> = {
  fr: {
    features: 'Nouveautés',
    bugfixes: 'Corrections'
  },
  en: {
    features: 'Features',
    bugfixes: 'Fixes'
  }
}

const isReleaseNote = (note: Partial<FileReleaseNote>): note is ReleaseNote => {
  return Boolean(note.version && typeof note.version === 'string' && note.changes)
}

const compareVersionsDesc = (versionA: string, versionB: string) => {
  const partsA = versionA.split('.').map(Number)
  const partsB = versionB.split('.').map(Number)
  const length = Math.max(partsA.length, partsB.length)

  for (let index = 0; index < length; index += 1) {
    const partA = partsA[index] || 0
    const partB = partsB[index] || 0

    if (partA !== partB) return partB - partA
  }

  return 0
}

const getReleaseTimestamp = (date: string | null) => {
  if (!date) return 0

  const timestamp = new Date(date.replace(' ', 'T')).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const compareReleaseNotesDesc = (noteA: ReleaseNote, noteB: ReleaseNote) => {
  const dateComparison = getReleaseTimestamp(noteB.date) - getReleaseTimestamp(noteA.date)

  if (dateComparison !== 0) return dateComparison

  return compareVersionsDesc(noteA.version, noteB.version)
}

export const normalizeReleaseNotes = (data: Partial<FileReleaseNote>[] = []): ReleaseNote[] => {
  return data
    .filter(isReleaseNote)
    .map(note => ({
      source: note.source,
      version: note.version,
      date: note.date || null,
      changes: note.changes || {}
    }))
    .sort(compareReleaseNotesDesc)
}

export const RELEASE_NOTES = normalizeReleaseNotes(releaseNotesData)

export const getLanguage = (language?: string) => language?.split('-')[0] || 'fr'

export const getChangeTypeLabel = (type: string, language?: string) => {
  const currentLanguage = getLanguage(language)
  return CHANGE_TYPE_LABELS[currentLanguage]?.[type] || CHANGE_TYPE_LABELS.fr[type] || type
}

export const getLocalizedChanges = (changes: LocalizedChanges, language?: string) => {
  const currentLanguage = getLanguage(language)
  return changes[currentLanguage] || changes.en || {}
}

export const formatReleaseDate = (date: string | null, language?: string) => {
  if (!date) return '-'

  const parsedDate = new Date(date.replace(' ', 'T'))
  if (Number.isNaN(parsedDate.getTime())) return date

  const currentLanguage = language || 'fr'
  const formattedDate = new Intl.DateTimeFormat(currentLanguage, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(parsedDate)
  const formattedTime = new Intl.DateTimeFormat(currentLanguage, {
    hour: '2-digit',
    minute: '2-digit'
  }).format(parsedDate)

  return `${formattedDate} ${formattedTime}`
}
